import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { FileUploadingStatus } from 'enums/shared/fileUploadingStatus';
import { FC, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnmount, useUpdate, useUpdateEffect } from 'react-use';
import { isFetchBaseQueryError } from 'rtkQuery/isFetchBaseQueryError';
import { isSharesproFetchQueryError } from 'rtkQuery/sharespro/isSharesproFetchQueryError';
import { useSendMessageMutation } from 'rtkQuery/sharespro/queries/chat';
import { useUploadFileMutation } from 'rtkQuery/sharespro/queries/common';
import { isErrorWithMessage } from 'utils/isErrorWithMessage';

import ChatUploadingFile from 'presentation/components/modules/chat/detail/uploadingFile/ChatUploadingFile';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  filesToUpload: File[];
  chatId: ID;
  scrollEndRef: MutableRefObject<HTMLDivElement | null>;
}

const canceledQueues: Set<File[]> = new Set();
const succeeded: Set<File> = new Set();
const failed: Map<File, string> = new Map();
const uploaded: Map<File, ChatMessageUploadingFile> = new Map();
const canceled: Set<File> = new Set();

const UploadChatFilesModal: FC<Props> = ({
  isOpen,
  onClose,
  filesToUpload,
  chatId,
  scrollEndRef,
}) => {
  const { t } = useTranslation();

  const [uploadFileToApi] = useUploadFileMutation();

  const [sendMessage] = useSendMessageMutation();

  const forceRender = useUpdate();

  const getFileErrorText = (error: any): string => {
    if (isSharesproFetchQueryError(error)) {
      const errorData = error.data as any;
      const fieldErrors = errorData.field_errors;
      if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
        for (const fieldError of fieldErrors) {
          if (fieldError.field === 'file' && fieldError.error) {
            return fieldError.error;
          }
        }
      }

      return errorData.error_message;
    } else {
      if (isErrorWithMessage(error)) {
        return error.message;
      } else if (
        isFetchBaseQueryError(error) &&
        error.status === 'FETCH_ERROR'
      ) {
        return t('checkYourConnectionError');
      }
    }

    return t('somethingWentWrongPleaseContactSupport');
  };

  const allFilesUploaded = (): boolean => {
    return (
      canceled.size + uploaded.size === filesToUpload.length &&
      failed.size === 0
    );
  };

  const filteredFiles = filesToUpload.filter((file) => !canceled.has(file));

  const sendFilesToChat = (): void => {
    sendMessage({
      chat_id: chatId,
      files: Array.from(uploaded.values()),
    });
    queueMicrotask(() => {
      scrollEndRef.current?.scrollIntoView();
    });
  };

  const uploadFile = async (file: File): Promise<void> => {
    if (!validateFile(file)) {
      forceRender();

      return;
    }

    try {
      const resp = await uploadFileToApi(file).unwrap();
      if (canceled.has(file) || canceledQueues.has(filesToUpload)) {
        return;
      }

      succeeded.add(file);
      uploaded.set(file, {
        id: resp.data.id,
        url: resp.data.url,
        name: file.name,
        size: file.size,
      });

      if (allFilesUploaded()) {
        sendFilesToChat();
        onClose();
      }
    } catch (err) {
      failed.set(file, getFileErrorText(err));
    } finally {
      forceRender();
    }
  };

  const validateFile = (file: File): boolean => {
    if (file.size > 31024 * 1024 * 100) {
      failed.set(file, t('fileIsTooBigMb', { name: file.name, size: 100 }));

      return false;
    }

    return true;
  };

  useUpdateEffect(() => {
    clearData();
    (async () => {
      if (filesToUpload.length > 0) {
        for (const file of filesToUpload) {
          validateFile(file);
        }

        for (const file of filesToUpload) {
          if (canceledQueues.has(filesToUpload)) {
            canceledQueues.delete(filesToUpload);

            break;
          }
          if (failed.has(file) || succeeded.has(file)) continue;
          await uploadFile(file);
        }
        if (canceledQueues.has(filesToUpload)) {
          canceledQueues.delete(filesToUpload);
        }
      }
    })();
  }, [filesToUpload]);

  const cancelUploading = (): void => {
    canceledQueues.add(filesToUpload);
    onClose();
  };

  const clearData = (): void => {
    succeeded.clear();
    failed.clear();
    uploaded.clear();
    canceled.clear();
  };

  useUpdateEffect(() => {
    if (!isOpen) {
      clearData();
    }
  }, [isOpen]);

  useUnmount(() => {
    clearData();
  });

  return (
    <Modal isOpen={isOpen} onClose={cancelUploading}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('filesUploading')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {filteredFiles.length > 0 ? (
            filteredFiles.map((file, index) => {
              let status;
              if (succeeded.has(file)) {
                status = FileUploadingStatus.uploaded;
              } else if (failed.has(file)) {
                status = FileUploadingStatus.failed;
              } else {
                status = FileUploadingStatus.uploading;
              }

              return (
                <ChatUploadingFile
                  key={index}
                  status={status}
                  name={file.name}
                  size={file.size}
                  cancel={() => {
                    canceled.add(file);
                    failed.delete(file);
                    uploaded.delete(file);
                    forceRender();
                    if (allFilesUploaded()) {
                      if (uploaded.size > 0) {
                        sendFilesToChat();
                      }
                      cancelUploading();
                    }
                  }}
                  repeat={() => {
                    failed.delete(file);
                    forceRender();
                    uploadFile(file);
                  }}
                  error={failed.get(file)}
                />
              );
            })
          ) : (
            <Text>{t('listIsEmpty')}</Text>
          )}
        </ModalBody>

        <ModalFooter>
          <Button mr={3} onClick={cancelUploading}>
            {t('cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UploadChatFilesModal;
